import CreateByClassification from "components/modal/bidDocuments/createByClassification";
import CreateDocument from "components/modal/bidDocuments/createDocument";
import CreateUniversalTransferDocument from "components/modal/bidDocuments/createUniversalTransferDocument";
import OrganizationRequiredInfoModal from "components/modal/organizationRequiredInfo";
import { documentBusinessTypesEnum, relationToTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import React from "react";
import { alert } from "services/alert";
import {
    createAcceptanceCertificate,
    createAcceptanceCertificateInvoice,
    createDocumentFromTemplate,
    createInvoice,
    getBid,
    getClassificationTypePreview,
} from "sources/books/bids";
import {
    createAcceptanceCertificate as createDistributionAcceptanceCertificate,
    createAcceptanceCertificateInvoice as createDistributionAcceptanceCertificateInvoice,
    createDocumentFromTemplate as createDistributionDocumentFromTemplate,
    createInvoice as createDistributionInvoice,
    createHiredCounterpartyDocumentFromTemplate,
    getDistributionBid,
    getClassificationTypePreview as getDistributionClassificationTypePreview,
} from "sources/books/distributionBids";
import { createUniversalTransferDocument } from "sources/books/universalTransferDocuments";
import { getInfoToCreateDocument } from "sources/profile/organization";

const documentDateField = {
    label: "Дата документа",
    inputProps: { name: "documentDate", type: "date" },
    type: "date",
};

const invoiceBasisFields = [
    {
        label: "Номер документа-основания",
        inputProps: { name: "invoiceDocumentBasis" },
        type: "text",
    },
    {
        label: "Дата документа-основания",
        inputProps: { name: "invoiceDocumentBasisDate", type: "date" },
        type: "date",
    },
];

const actBasisFields = [
    {
        label: "Номер документа-основания",
        inputProps: { name: "documentBasis" },
        type: "text",
    },
    {
        label: "Дата документа-основания",
        inputProps: { name: "documentBasisDate", type: "date" },
        type: "date",
    },
];

const createAction = (action, refresh) => (reqData) =>
    action(reqData).then((res) => {
        if (res) {
            refresh();
        }
        return res;
    });

const createActionWithRequiredInfo = (add, roles, action, refresh) => (reqData) => {
    const actionWithUpdate = createAction(action, refresh);

    if (reqData?.withoutSigningFiles) {
        return actionWithUpdate(reqData);
    }

    return getInfoToCreateDocument()
        .then((res) => {
            if (res == null) {
                return actionWithUpdate(reqData);
            }

            if (res.hasUnfilledRequiredFields || res.hasUnfilledFinancialRequiredFields) {
                return new Promise((resolve) => {
                    add({
                        name: "default",
                        title: "Заполните реквизиты",
                        windowMod: "sm",
                        CustomBody: OrganizationRequiredInfoModal,
                        onCloseAction: () => setTimeout(() => resolve(false), 0),
                        bodyData: {
                            data: res,
                            onSuccess: () => resolve(actionWithUpdate(reqData)),
                            roles,
                        },
                    });
                });
            }

            return actionWithUpdate(reqData);
        })
        .catch(() => actionWithUpdate(reqData));
};

const openWithRequiredInfo = (modal, roles, openForm) => {
    getInfoToCreateDocument()
        .then((res) => {
            if (res == null) {
                openForm();
                return;
            }

            if (res.hasUnfilledRequiredFields || res.hasUnfilledFinancialRequiredFields) {
                modal({
                    name: "default",
                    title: "Заполните реквизиты",
                    windowMod: "sm",
                    CustomBody: OrganizationRequiredInfoModal,
                    bodyData: {
                        data: res,
                        onSuccess: openForm,
                        roles,
                    },
                });
            } else {
                openForm();
            }
        })
        .catch(openForm);
};

const getRelatedId = (item) => item.relatedToId || item.bidId;

const getRelatedEntity = (item) => {
    const id = getRelatedId(item);
    if (!id) {
        return Promise.resolve(false);
    }

    return item.relatedToType === relationToTypesEnum.DistributionBid ? getDistributionBid(id) : getBid(id);
};

const getDocumentConfig = (documentType, relatedToType, refresh, roles) => {
    const isDistributionBid = relatedToType === relationToTypesEnum.DistributionBid;

    switch (documentType) {
        case documentBusinessTypesEnum.Contract:
            return {
                title: (
                    <div className="d-flex align-items-baseline">
                        <span>Договор-заявка</span>
                        <a
                            className="font-size-14 ml-3"
                            href={paths.documentTemplates}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Шаблоны документов
                        </a>
                    </div>
                ),
                Component: CreateDocument,
                action: isDistributionBid ? createDistributionDocumentFromTemplate : createDocumentFromTemplate,
                windowMod: "sm",
            };
        case documentBusinessTypesEnum.Invoice:
            return {
                title: "Счета",
                Component: (props) => (
                    <CreateByClassification
                        {...props}
                        action={createActionWithRequiredInfo(
                            props.add,
                            roles,
                            isDistributionBid ? createDistributionInvoice : createInvoice,
                            refresh,
                        )}
                    />
                ),
                previewAction: isDistributionBid
                    ? getDistributionClassificationTypePreview
                    : getClassificationTypePreview,
                manualValues: invoiceBasisFields,
                windowMod: "sm",
            };
        case documentBusinessTypesEnum.AcceptanceCertificate:
            return {
                title: "Акты",
                Component: (props) => (
                    <CreateByClassification
                        {...props}
                        action={createActionWithRequiredInfo(
                            props.add,
                            roles,
                            isDistributionBid ? createDistributionAcceptanceCertificate : createAcceptanceCertificate,
                            refresh,
                        )}
                    />
                ),
                previewAction: isDistributionBid
                    ? getDistributionClassificationTypePreview
                    : getClassificationTypePreview,
                manualValues: actBasisFields,
                windowMod: "sm",
            };
        case documentBusinessTypesEnum.TaxInvoice:
            return {
                title: "Счет-фактуры",
                Component: (props) => (
                    <CreateByClassification
                        {...props}
                        action={createActionWithRequiredInfo(
                            props.add,
                            roles,
                            isDistributionBid
                                ? createDistributionAcceptanceCertificateInvoice
                                : createAcceptanceCertificateInvoice,
                            refresh,
                        )}
                    />
                ),
                previewAction: isDistributionBid
                    ? getDistributionClassificationTypePreview
                    : getClassificationTypePreview,
                windowMod: "sm",
            };
        default:
            return null;
    }
};

const getContractBodyData = (config, item, relatedEntity, refresh) => {
    if (item.relatedToType === relationToTypesEnum.DistributionBid) {
        return {
            id: relatedEntity.id,
            documentDate: documentDateField,
            targets: [
                {
                    key: "counterparty",
                    label: "С заказчиком",
                    buttonText: "Создать договор с заказчиком",
                    counterparty: relatedEntity.counterparty,
                    action: createAction(createDistributionDocumentFromTemplate, refresh),
                    missingCounterpartyMessage:
                        "Для создания договора-заявки с заказчиком необходимо выбрать контрагента заказа",
                },
                {
                    key: "hiredCounterparty",
                    label: "С перевозчиком",
                    buttonText: "Создать договор с перевозчиком",
                    counterparty: relatedEntity.hiredCarCounterparty,
                    action: createAction(createHiredCounterpartyDocumentFromTemplate, refresh),
                    missingCounterpartyMessage:
                        "Для создания договора-заявки с перевозчиком необходимо выбрать перевозчика",
                },
            ],
        };
    }

    const counterparty = relatedEntity.cargoOwnerDictionaryItem || relatedEntity.counterparty;
    return {
        id: relatedEntity.id,
        documentDate: documentDateField,
        cargoOwnerDictionaryItem: counterparty,
        counterparty,
        action: createAction(config.action, refresh),
    };
};

export const openUniversalDocumentCreateForm = ({ item, documentType, modal, roles, refresh, setLoading }) => {
    if (!getRelatedId(item)) {
        alert({ type: "warning", title: "Ошибка", message: "Не удалось определить заявку или заказ" });
        return;
    }

    setLoading(true);
    getRelatedEntity(item)
        .then((relatedEntity) => {
            if (!relatedEntity) {
                alert({ type: "warning", title: "Ошибка", message: "Не удалось загрузить заявку или заказ" });
                return;
            }

            const relatedToType = item.relatedToType || relationToTypesEnum.Bid;
            const config = getDocumentConfig(documentType, relatedToType, refresh, roles);

            if (!config) {
                alert({ type: "warning", title: "Ошибка", message: "Неподдерживаемый тип документа" });
                return;
            }

            const openForm = () => {
                modal({
                    name: "default",
                    title: config.title,
                    windowMod: config.windowMod,
                    CustomBody: config.Component,
                    bodyData:
                        documentType === documentBusinessTypesEnum.Contract
                            ? getContractBodyData(config, item, relatedEntity, refresh)
                            : {
                                  id: relatedEntity.id,
                                  documentDate: documentDateField,
                                  manualValues: config.manualValues,
                                  previewAction: config.previewAction,
                              },
                });
            };

            if (documentType === documentBusinessTypesEnum.Contract) {
                openWithRequiredInfo(modal, roles, openForm);
            } else {
                openForm();
            }
        })
        .finally(() => setLoading(false));
};

export const openUniversalTransferDocumentCreateForm = ({ item, modal, roles, refresh }) => {
    const bidId = item.bidId || item.relatedToId;

    if (!bidId) {
        alert({ type: "warning", title: "Ошибка", message: "Не удалось определить заявку" });
        return;
    }

    modal({
        name: "default",
        title: "УПД",
        windowMod: "sm",
        CustomBody: (props) => (
            <CreateUniversalTransferDocument
                {...props}
                action={createActionWithRequiredInfo(props.add, roles, createUniversalTransferDocument, refresh)}
            />
        ),
        bodyData: {
            id: bidId,
        },
    });
};
